import {
  ApplicationSettingsType,
  AuthenticationClientType,
  AuthenticationUserType,
  CashPoolingProfile,
} from '@fides/fides-react-components';
import { JsonObject } from '../types';
import { Application } from './Application';
import { Authority } from './Authority';
import { UserSettings } from './UserSettings';

interface SimplifiedPaymentScreenSettings {
  orderId: boolean;
  transactionCurrency: boolean;
  transactionDefaultCurrency: string;
  transactionRemittanceInfo: boolean;
  transactionEndToEndId: boolean;
  transactionCharges: boolean;
  transactionGroupCategoryPurpose: boolean;
  transactionGroupPriority: boolean;
  transactionService: boolean;
  transactionGroupExecutionDate: boolean;
}

interface ApplicationSettings extends ApplicationSettingsType {
  simplifiedPaymentScreenSettings: SimplifiedPaymentScreenSettings;
}

export type Client = AuthenticationClientType & {
  applicationSettings: AuthenticationClientType['applicationSettings'] & ApplicationSettings;
};

export class User {
  public id: string;
  public clientId: string;
  public fullName: string;
  public userName: string;
  public enabled: boolean;
  public twoMansRule: boolean;
  public secondFactorAuthenticated: boolean;
  public setSecondFactorAuthenticationType: string;
  public securityCodeValidUntil?: Date;
  public previousLogin?: Date;
  public passwordChangeRequired: boolean;
  public passwordExpired: boolean;
  public settings: UserSettings;
  public applications: Application[];
  public authorities: Authority[];
  public impersonated: boolean;
  public impersonatedClient: Client;
  public isClientCPSPartner: boolean;
  public isClientCPSCorporate: boolean;
  public isPartnerEndUser: boolean;
  public isCorporateEndUser: boolean;

  public impersonatedUser: User;
  public client: Client;
  public userGroups: Authority[];
  public domainUser: AuthenticationUserType['domainUser'];
  public sessionId: string;
  public b2cMfa: boolean;

  constructor(json: JsonObject) {
    this.id = json.id;
    this.clientId = json.clientId;
    this.fullName = json.domainUser.fullName;
    this.domainUser = json.domainUser;
    this.userName = json.username;
    this.enabled = json.enabled;
    this.userGroups = json.userGroups;
    this.secondFactorAuthenticated = json.secondFactorAuthenticated;
    this.previousLogin = new Date(json.previousLogin * 1000);
    this.setSecondFactorAuthenticationType = json.setSecondFactorAuthenticationType;
    this.passwordChangeRequired = json.passwordChangeRequired;
    this.passwordExpired = json.passwordExpired;
    this.client = json.client;
    this.twoMansRule = json.twoMansRule;
    this.settings = new UserSettings(json);
    this.applications = json.applications
      ? json.applications.map((item: JsonObject) => ({
          name: item.name,
          url: item.url,
          enabled: item.enabled,
        }))
      : [];
    this.userGroups = json.userGroups?.map((item: JsonObject) => new Authority(item));
    this.domainUser = json.domainUser;
    this.authorities = json.authorities?.map((item: JsonObject) => new Authority(item));
    this.impersonated = json.impersonated;
    this.impersonatedClient = json.impersonatedClient;
    this.impersonatedUser = json.impersonatedUser;
    this.isClientCPSPartner = json.client.applicationSettings.settings.cpsProfile === CashPoolingProfile.PARTNER;
    this.isClientCPSCorporate = json.client.applicationSettings.settings.cpsProfile === CashPoolingProfile.CORPORATE;
    this.sessionId = json.sessionId;
    this.b2cMfa = json.b2cMfa;
  }
}
